// =============================================================================
// PAGES/NOVASOPERACOES.TSX - Inventário de grupos do iSolve com CCV assinado
// =============================================================================
// Lista TODOS os grupos (idCstimob) com CCV assinado no iSolve - inclusive os
// concluídos/mortos e os já existentes no CRM. A Carga inicial (baseline) popula
// sem criar; o Sincronizar cria automaticamente o que for genuinamente novo.
// "Concluir" marca a linha (REJEITADO) SEM removê-la - controle/auditoria.
// =============================================================================

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import {
  novasOperacoesApi,
  type CandidataNovaOperacao,
  type ContadoresNovasOperacoes,
  type FiltrosNovasOperacoes,
} from '../services/api';

function formatarData(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('pt-BR');
}

function formatarValor(v: number | null): string {
  if (v == null) return '-';
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}

const ROTULO_CATEGORIA: Record<string, string> = {
  ELEGIVEL: 'Elegível',
  CONCLUIDA_ISOLVE: 'Concluída no iSolve',
  MORTA: 'Morta (>90d)',
};

const COR_CATEGORIA: Record<string, string> = {
  ELEGIVEL: 'bg-emerald-100 text-emerald-700',
  CONCLUIDA_ISOLVE: 'bg-slate-200 text-slate-600',
  MORTA: 'bg-slate-200 text-slate-500',
};

const ROTULO_ESTADO: Record<string, string> = {
  PENDENTE: 'A decidir',
  APROVADO: 'Criada',
  REJEITADO: 'Concluída',
};

const COR_ESTADO: Record<string, string> = {
  PENDENTE: 'bg-blue-100 text-blue-700',
  APROVADO: 'bg-emerald-100 text-emerald-700',
  REJEITADO: 'bg-amber-100 text-amber-700',
};

export default function NovasOperacoes() {
  const navigate = useNavigate();
  const [itens, setItens] = useState<CandidataNovaOperacao[]>([]);
  const [contadores, setContadores] = useState<ContadoresNovasOperacoes | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [processando, setProcessando] = useState(false);
  const [selecionadas, setSelecionadas] = useState<Set<string>>(new Set());
  const [detalheItem, setDetalheItem] = useState<CandidataNovaOperacao | null>(null);

  const [filtroEstado, setFiltroEstado] = useState<'' | 'PENDENTE' | 'APROVADO' | 'REJEITADO'>('');
  const [filtroCategoria, setFiltroCategoria] = useState<string>('');
  const [busca, setBusca] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const filtros: FiltrosNovasOperacoes = {};
      if (filtroEstado) filtros.status = filtroEstado;
      if (filtroCategoria) filtros.categoria = filtroCategoria;
      if (busca.trim()) filtros.busca = busca.trim();
      const r = await novasOperacoesApi.listar(filtros);
      setItens(r.itens);
      setContadores(r.contadores);
    } catch {
      toast.error('Erro ao carregar o inventário de operações.');
    } finally {
      setCarregando(false);
    }
  }, [filtroEstado, filtroCategoria, busca]);

  useEffect(() => { carregar(); }, [carregar]);

  const toggle = (id: string) =>
    setSelecionadas((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });

  const idsSelecionadas = [...selecionadas];

  const criar = async (ids: string[]) => {
    if (ids.length === 0) return;
    setProcessando(true);
    try {
      const r = await novasOperacoesApi.criar(ids);
      const ok = r.resultados.filter((x) => x.ok);
      const falhas = r.resultados.filter((x) => !x.ok);
      const comAviso = ok.filter((x) => x.aviso);
      if (falhas.length > 0) {
        toast.warning(
          `${ok.length} criada(s). Falhas: ${falhas.map((f) => `${f.idCstimob}: ${f.motivo}`).join(' | ')}`,
          { duration: 10000 },
        );
      } else if (comAviso.length > 0) {
        toast.warning(`${ok.length} processada(s). ${comAviso[0].aviso}`, { duration: 8000 });
      } else {
        toast.success(`${ok.length} operação(ões) criada(s).`);
      }
      setSelecionadas(new Set());
      carregar();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { erro?: string } }; message?: string };
      toast.error(err?.response?.data?.erro ?? err?.message ?? 'Erro ao criar operação(ões).');
    } finally {
      setProcessando(false);
    }
  };

  const concluir = async (ids: string[]) => {
    if (ids.length === 0) return;
    setProcessando(true);
    try {
      await novasOperacoesApi.concluir(ids);
      toast.success(`${ids.length} marcada(s) como concluída(s).`);
      setSelecionadas(new Set());
      carregar();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { erro?: string } }; message?: string };
      toast.error(err?.response?.data?.erro ?? err?.message ?? 'Erro ao concluir.');
    } finally {
      setProcessando(false);
    }
  };

  const reabrir = async (ids: string[]) => {
    if (ids.length === 0) return;
    setProcessando(true);
    try {
      await novasOperacoesApi.reabrir(ids);
      toast.success(`${ids.length} reaberta(s).`);
      setSelecionadas(new Set());
      carregar();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { erro?: string } }; message?: string };
      toast.error(err?.response?.data?.erro ?? err?.message ?? 'Erro ao reabrir.');
    } finally {
      setProcessando(false);
    }
  };

  const todasSelecionadas = itens.length > 0 && selecionadas.size === itens.length;

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#F0F2F5' }}>
      <main className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <div className="px-4 py-3 bg-white border-b border-slate-200">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="text-lg font-semibold text-slate-800">Entrada iSolve</h1>
              <p className="text-xs text-slate-500">
                Inventário de todos os grupos com CCV assinado no iSolve. Criar = vira operação no CRM; Concluir = marca e mantém para auditoria.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => criar(idsSelecionadas)}
                disabled={processando || idsSelecionadas.length === 0}
                className="text-xs font-medium text-white bg-[#F49519] px-3 py-1.5 rounded-lg hover:bg-[#D97B0B] disabled:opacity-50 transition-colors"
              >
                Criar selecionadas
              </button>
              <button
                onClick={() => concluir(idsSelecionadas)}
                disabled={processando || idsSelecionadas.length === 0}
                className="text-xs font-medium text-slate-600 bg-white border border-slate-300 px-3 py-1.5 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
              >
                Concluir selecionadas
              </button>
            </div>
          </div>

          {contadores && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              <span className="px-2 py-1 rounded-lg bg-slate-100 text-slate-700">Total: <strong>{contadores.total}</strong></span>
              <span className="px-2 py-1 rounded-lg bg-blue-100 text-blue-700">A decidir: <strong>{contadores.pendentes}</strong></span>
              <span className="px-2 py-1 rounded-lg bg-emerald-100 text-emerald-700">Criadas: <strong>{contadores.criadas}</strong></span>
              <span className="px-2 py-1 rounded-lg bg-amber-100 text-amber-700">Concluídas: <strong>{contadores.concluidas}</strong></span>
              <span className="px-2 py-1 rounded-lg bg-slate-100 text-slate-600">Já no CRM: <strong>{contadores.jaNoCrm}</strong></span>
              <span className="px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200">Elegíveis p/ criar: <strong>{contadores.elegiveis}</strong></span>
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value as '' | 'PENDENTE' | 'APROVADO' | 'REJEITADO')}
              className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 bg-white"
            >
              <option value="">Todos os estados</option>
              <option value="PENDENTE">A decidir</option>
              <option value="APROVADO">Criada</option>
              <option value="REJEITADO">Concluída</option>
            </select>

            <select
              value={filtroCategoria}
              onChange={(e) => setFiltroCategoria(e.target.value)}
              className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 bg-white"
            >
              <option value="">Todas as categorias</option>
              <option value="ELEGIVEL">Elegível</option>
              <option value="CONCLUIDA_ISOLVE">Concluída no iSolve</option>
              <option value="MORTA">Morta (&gt;90d)</option>
            </select>

            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por cliente ou idCstimob"
              className="text-xs border border-slate-300 rounded-lg px-3 py-1.5 bg-white flex-1 min-w-[200px]"
            />

            {carregando && <span className="text-xs text-slate-400">carregando...</span>}
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-auto px-4 py-3">
          {itens.length === 0 && !carregando && (
            <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500 text-sm">
              Nenhum item para os filtros atuais. Rode a Carga inicial (baseline) ou o Sincronizar.
            </div>
          )}

          {itens.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left text-xs text-slate-500 border-b border-slate-200 whitespace-nowrap">
                    <th className="px-3 py-2 w-8">
                      <input
                        type="checkbox"
                        checked={todasSelecionadas}
                        onChange={(e) => setSelecionadas(e.target.checked ? new Set(itens.map((i) => i.idCstimob)) : new Set())}
                        className="rounded"
                      />
                    </th>
                    <th className="px-3 py-2">idCstimob</th>
                    <th className="px-3 py-2">Cliente</th>
                    <th className="px-3 py-2">CCV em</th>
                    <th className="px-3 py-2">Situação iSolve</th>
                    <th className="px-3 py-2">Categoria</th>
                    <th className="px-3 py-2">Estado</th>
                    <th className="px-3 py-2">Valor</th>
                    <th className="px-3 py-2">Parceiro</th>
                    <th className="px-3 py-2 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {itens.map((item) => (
                    <tr
                      key={item.id}
                      onClick={() => setDetalheItem(item)}
                      className="border-b border-slate-100 hover:bg-slate-50 whitespace-nowrap cursor-pointer"
                    >
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selecionadas.has(item.idCstimob)}
                          onChange={() => toggle(item.idCstimob)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{item.idCstimob}</td>
                      <td className="px-3 py-2">{item.cliente ?? '-'}</td>
                      <td className="px-3 py-2 text-slate-500">{formatarData(item.dataAssinaturaCCV)}</td>
                      <td className="px-3 py-2 text-slate-600">{item.situacao ?? '-'}</td>
                      <td className="px-3 py-2">
                        {item.categoria ? (
                          <span className={'text-xs px-2 py-0.5 rounded-full ' + (COR_CATEGORIA[item.categoria] ?? 'bg-slate-100 text-slate-600')}>
                            {ROTULO_CATEGORIA[item.categoria] ?? item.categoria}
                          </span>
                        ) : '-'}
                        {item.jaNoCrm && (
                          <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Já no CRM</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className={'text-xs px-2 py-0.5 rounded-full ' + (COR_ESTADO[item.status] ?? 'bg-slate-100 text-slate-600')}>
                          {ROTULO_ESTADO[item.status] ?? item.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-500 text-xs">{formatarValor(item.valor)}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs">{item.parceiro ?? '-'}</td>
                      <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {item.status === 'PENDENTE' && !item.jaNoCrm && (
                          <button
                            onClick={() => criar([item.idCstimob])}
                            disabled={processando}
                            className="text-xs font-medium text-white bg-[#F49519] px-3 py-1 rounded-lg hover:bg-[#D97B0B] disabled:opacity-50 transition-colors"
                          >
                            Criar
                          </button>
                        )}
                        {item.status === 'PENDENTE' && (
                          <button
                            onClick={() => concluir([item.idCstimob])}
                            disabled={processando}
                            className="text-xs font-medium text-slate-600 bg-white border border-slate-300 px-3 py-1 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
                          >
                            Concluir
                          </button>
                        )}
                        {item.status !== 'PENDENTE' && (
                          <button
                            onClick={() => reabrir([item.idCstimob])}
                            disabled={processando}
                            className="text-xs font-medium text-slate-600 bg-white border border-slate-300 px-3 py-1 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
                          >
                            Reabrir
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Modal lateral de detalhe do item (abre ao clicar na linha) */}
      <Sheet open={!!detalheItem} onOpenChange={(aberto) => { if (!aberto) setDetalheItem(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          {detalheItem && (
            <>
              <SheetHeader>
                <SheetTitle>Candidata a operação</SheetTitle>
                <SheetDescription className="font-mono text-xs">{detalheItem.idCstimob}</SheetDescription>
              </SheetHeader>

              <dl className="mt-5 space-y-3 text-sm">
                <CampoDetalhe rotulo="Cliente" valor={detalheItem.cliente ?? '-'} />
                <div className="flex justify-between gap-3">
                  <dt className="text-slate-500">Estado</dt>
                  <dd>
                    <span className={'text-xs px-2 py-0.5 rounded-full ' + (COR_ESTADO[detalheItem.status] ?? 'bg-slate-100 text-slate-600')}>
                      {ROTULO_ESTADO[detalheItem.status] ?? detalheItem.status}
                    </span>
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-slate-500">Categoria</dt>
                  <dd className="flex items-center gap-1">
                    {detalheItem.categoria ? (
                      <span className={'text-xs px-2 py-0.5 rounded-full ' + (COR_CATEGORIA[detalheItem.categoria] ?? 'bg-slate-100 text-slate-600')}>
                        {ROTULO_CATEGORIA[detalheItem.categoria] ?? detalheItem.categoria}
                      </span>
                    ) : '-'}
                    {detalheItem.jaNoCrm && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Já no CRM</span>
                    )}
                  </dd>
                </div>
                <CampoDetalhe rotulo="Situação iSolve" valor={detalheItem.situacao ?? '-'} />
                <CampoDetalhe rotulo="CCV assinado em" valor={formatarData(detalheItem.dataAssinaturaCCV)} />
                <CampoDetalhe rotulo="Cadastro no iSolve" valor={formatarData(detalheItem.dataCadastro)} />
                <CampoDetalhe rotulo="Valor" valor={formatarValor(detalheItem.valor)} />
                <CampoDetalhe rotulo="Parceiro" valor={detalheItem.parceiro ?? '-'} />
                <CampoDetalhe rotulo="Importada em" valor={formatarData(detalheItem.criadoEm)} />
                <CampoDetalhe rotulo="Atualizado no iSolve" valor={formatarData(detalheItem.atualizadoEmMeta)} />
              </dl>

              <div className="mt-6 flex flex-wrap gap-2">
                {detalheItem.operacaoId && (
                  <button
                    onClick={() => navigate(`/operacoes/${detalheItem.operacaoId}`)}
                    className="text-xs font-medium text-white bg-otm-navy px-3 py-1.5 rounded-lg hover:opacity-90 transition-opacity"
                  >
                    Abrir operação no CRM
                  </button>
                )}
                {detalheItem.status === 'PENDENTE' && !detalheItem.jaNoCrm && (
                  <button
                    onClick={async () => { await criar([detalheItem.idCstimob]); setDetalheItem(null); }}
                    disabled={processando}
                    className="text-xs font-medium text-white bg-[#F49519] px-3 py-1.5 rounded-lg hover:bg-[#D97B0B] disabled:opacity-50 transition-colors"
                  >
                    Criar operação
                  </button>
                )}
                {detalheItem.status === 'PENDENTE' && (
                  <button
                    onClick={async () => { await concluir([detalheItem.idCstimob]); setDetalheItem(null); }}
                    disabled={processando}
                    className="text-xs font-medium text-slate-600 bg-white border border-slate-300 px-3 py-1.5 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
                  >
                    Concluir
                  </button>
                )}
                {detalheItem.status !== 'PENDENTE' && (
                  <button
                    onClick={async () => { await reabrir([detalheItem.idCstimob]); setDetalheItem(null); }}
                    disabled={processando}
                    className="text-xs font-medium text-slate-600 bg-white border border-slate-300 px-3 py-1.5 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
                  >
                    Reabrir
                  </button>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// Linha rótulo/valor do detalhe lateral
function CampoDetalhe({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-500 shrink-0">{rotulo}</dt>
      <dd className="text-slate-800 text-right break-words">{valor}</dd>
    </div>
  );
}